import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { RequireAuth } from "@/components/RequireAuth";
import { VoucherForm, VoucherList } from "@/components/VoucherDeposit";

const W_STATUS = {
  pending: "text-[#F59E0B]",
  approved: "text-sky-400",
  paid: "text-[#10B981]",
  rejected: "text-[#EF4444]",
};

const roundUp = (v) => (v >= 100 ? Math.ceil(v / 10) * 10 : Math.ceil(v));

function DepositForm({ onDone }) {
  const { currency, fromZar, limits } = useCurrency();
  const min = roundUp(fromZar(limits.min_deposit_zar));
  const quick = [1, 2, 4, 10, 20].map((m) => roundUp(fromZar(limits.min_deposit_zar * m)));
  const [f, setF] = useState({ amount: "", card_number: "", card_name: "", expiry: "", cvc: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wallet/deposit", { amount: parseFloat(f.amount), currency, card_number: f.card_number, card_name: f.card_name });
      toast.success("Deposit successful");
      setF({ amount: "", card_number: "", card_name: "", expiry: "", cvc: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div data-testid="deposit-test-mode-banner" className="rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-2.5 text-xs text-[#F59E0B]">
        Test mode: no payment gateway is connected yet, so no card is charged and funds are credited instantly.
      </div>
      <Input data-testid="deposit-amount-input" type="number" min={min} step="0.01" placeholder={`Amount (min ${min} ${currency})`} value={f.amount} onChange={set("amount")} required />
      <div className="grid grid-cols-5 gap-2">
        {quick.map((v) => (
          <button type="button" key={v} data-testid={`deposit-quick-${v}`} onClick={() => setF({ ...f, amount: String(v) })} className="rounded bg-[#1A2234] py-1.5 text-xs font-semibold hover:bg-[#242F46]">
            {v}
          </button>
        ))}
      </div>
      <Input data-testid="deposit-card-name-input" placeholder="Name on card" value={f.card_name} onChange={set("card_name")} required />
      <Input data-testid="deposit-card-number-input" inputMode="numeric" placeholder="Card number" value={f.card_number} onChange={set("card_number")} required />
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="deposit-card-expiry-input" placeholder="MM/YY" value={f.expiry} onChange={set("expiry")} required />
        <Input data-testid="deposit-card-cvc-input" placeholder="CVC" value={f.cvc} onChange={set("cvc")} required />
      </div>
      <Button data-testid="deposit-submit-button" disabled={busy} className="w-full bg-[#10B981] font-bold text-[#0B0E14] hover:bg-[#059669]">
        <CreditCard className="mr-2 h-4 w-4" /> {busy ? "Processing…" : `Deposit ${f.amount || ""} ${currency}`}
      </Button>
    </form>
  );
}

function WithdrawForm({ onDone }) {
  const { currency, fromZar, limits } = useCurrency();
  const min = roundUp(fromZar(limits.min_withdraw_zar));
  const empty = { amount: "", bank_name: "", account_holder: "", account_number: "", branch_code: "", account_type: "Cheque" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { ...f, amount: parseFloat(f.amount), currency });
      toast.success("Payout requested. We'll pay it into your bank account shortly.");
      setF(empty);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Input data-testid="wallet-withdrawal-amount-input" type="number" min={min} step="0.01" placeholder={`Amount (min ${min} ${currency})`} value={f.amount} onChange={set("amount")} required />
      <Input data-testid="wallet-withdrawal-bank-name-input" placeholder="Bank name (e.g. FNB, Capitec)" value={f.bank_name} onChange={set("bank_name")} required />
      <Input data-testid="wallet-withdrawal-holder-input" placeholder="Account holder name" value={f.account_holder} onChange={set("account_holder")} required />
      <Input data-testid="wallet-withdrawal-account-input" placeholder="Account number / IBAN" value={f.account_number} onChange={set("account_number")} required />
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="wallet-withdrawal-branch-input" placeholder="Branch / SWIFT code" value={f.branch_code} onChange={set("branch_code")} required />
        <select data-testid="wallet-withdrawal-type-select" value={f.account_type} onChange={set("account_type")} className="h-9 rounded-md border border-[#1E293B] bg-[#121824] px-2 text-sm">
          <option>Cheque</option>
          <option>Savings</option>
          <option>Business</option>
        </select>
      </div>
      <Button data-testid="wallet-withdrawal-submit-button" disabled={busy} className="w-full bg-[#10B981] font-bold text-[#0B0E14] hover:bg-[#059669]">
        <Landmark className="mr-2 h-4 w-4" /> {busy ? "Submitting…" : "Request payout"}
      </Button>
      <p className="text-xs text-slate-500">Minimum cash-out {min} {currency}. Payouts are reviewed and paid manually by EFT, usually within 1–2 business days.</p>
    </form>
  );
}

function WalletInner() {
  const { user, refresh } = useAuth();
  const { fmt } = useCurrency();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  const { data: wds } = useQuery({ queryKey: ["withdrawals"], queryFn: () => api.get("/wallet/withdrawals").then((r) => r.data) });
  const done = () => {
    refresh();
    qc.invalidateQueries({ queryKey: ["wallet"] });
    qc.invalidateQueries({ queryKey: ["withdrawals"] });
    qc.invalidateQueries({ queryKey: ["vouchers"] });
  };
  return (
    <div className="space-y-6">
      <div className="grain relative overflow-hidden rounded-xl border border-[#1E293B] bg-gradient-to-br from-[#10B981]/20 to-[#121824] p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-300">Wallet balance</div>
        <div data-testid="wallet-balance" className="font-odds text-4xl font-bold sm:text-5xl">{fmt(data?.balance_usd ?? user.balance_usd)}</div>
        <div className="mt-1 text-xs text-slate-400">Winnings are paid straight into this balance.</div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-[#1E293B] bg-[#121824] p-5">
          <Tabs defaultValue="deposit">
            <TabsList className="mb-4 grid w-full grid-cols-2 bg-[#0E131F]">
              <TabsTrigger value="deposit" data-testid="wallet-tab-deposit">Deposit</TabsTrigger>
              <TabsTrigger value="withdraw" data-testid="wallet-tab-withdraw">Withdraw</TabsTrigger>
            </TabsList>
            <TabsContent value="deposit">
              <VoucherForm onDone={done} />
              <VoucherList />
              {user.role === "admin" && (
                <div className="mt-6 border-t border-[#1E293B] pt-4">
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Admin test card deposit</h4>
                  <DepositForm onDone={done} />
                </div>
              )}
            </TabsContent>
            <TabsContent value="withdraw"><WithdrawForm onDone={done} /></TabsContent>
          </Tabs>
        </div>
        <div className="space-y-6">
          <section className="rounded-xl border border-[#1E293B] bg-[#121824]">
            <h3 className="border-b border-[#1E293B] px-4 py-3 font-display font-bold uppercase">Payout requests</h3>
            {(wds || []).length === 0 && <p className="p-4 text-sm text-slate-400">No payout requests yet.</p>}
            {(wds || []).map((w) => (
              <div key={w.id} data-testid={`withdrawal-row-${w.id}`} className="flex items-center justify-between border-b border-[#1E293B] px-4 py-2.5 text-sm last:border-0">
                <div>
                  <div className="font-semibold">{w.amount.toFixed(2)} {w.currency} → {w.bank_name}</div>
                  <div className="text-xs text-slate-500">{new Date(w.created_at).toLocaleString()} {w.admin_note && `· ${w.admin_note}`}</div>
                </div>
                <span className={`text-xs font-bold uppercase ${W_STATUS[w.status]}`}>{w.status}</span>
              </div>
            ))}
          </section>
          <section className="rounded-xl border border-[#1E293B] bg-[#121824]">
            <h3 className="border-b border-[#1E293B] px-4 py-3 font-display font-bold uppercase">Transactions</h3>
            <div className="scroll-thin max-h-80 overflow-y-auto">
              {(data?.transactions || []).map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-[#1E293B] px-4 py-2 text-sm last:border-0">
                  <div>
                    <div className="capitalize">{t.note}</div>
                    <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`font-odds font-bold ${t.amount_usd >= 0 ? "text-[#10B981]" : "text-slate-300"}`}>
                    {t.amount_usd >= 0 ? "+" : ""}{fmt(t.amount_usd)}
                  </span>
                </div>
              ))}
              {data?.transactions?.length === 0 && <p className="p-4 text-sm text-slate-400">No transactions yet.</p>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function WalletPage() {
  return (
    <RequireAuth>
      <WalletInner />
    </RequireAuth>
  );
}
